import { Print } from "@mui/icons-material";
import {
  Button,
  type ButtonProps,
  type SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridValidRowModel,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";

/** Same value as MUI X `GRID_ROOT_GROUP_ID` � used as `rowNode.parent` for synthetic leaf nodes in print. */
const MUI_GRID_ROOT_GROUP_ID = "auto-generated-group-node-root" as const;

function buildPrintRenderCellParams(
  api: ReturnType<typeof useGridApiContext>["current"],
  row: GridValidRowModel,
  colDef: GridColDef,
): GridRenderCellParams {
  const id = (row as { rowId?: string | number }).rowId ?? (row as { id?: string | number }).id ?? "";
  const field = colDef.field;
  const value = row[field as keyof typeof row];

  return {
    api,
    cellMode: "view",
    colDef,
    field,
    formattedValue: value,
    hasFocus: false,
    id,
    isEditable: false,
    row,
    rowNode: {
      depth: 0,
      groupingKey: null,
      id,
      parent: MUI_GRID_ROOT_GROUP_ID,
      type: "leaf",
    },
    tabIndex: -1,
    value,
  } as GridRenderCellParams;
}

/** @param component Component is wrapped in `MaterialProvider` */
function onPrint(callback: () => void) {
  // we would not be here if `root` did not exist
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = document.getElementById("root")!;

  function onBeforePrint() {
    if (!root.classList.contains("print-widget-no-print")) {
      root.classList.add("print-widget-no-print");
      root.setAttribute("inert", "");
    }
  }

  function onAfterPrint() {
    if (root.classList.contains("print-widget-no-print")) {
      root.classList.remove("print-widget-no-print");
      root.removeAttribute("inert");
    }

    callback();
    window.removeEventListener("beforeprint", onBeforePrint);
    window.removeEventListener("afterprint", onAfterPrint);
  }

  window.addEventListener("beforeprint", onBeforePrint);
  window.addEventListener("afterprint", onAfterPrint);

  const interval = setInterval(function tryPrint() {
    if (document.getElementById("print-widget-print")) {
      window.print();
      clearInterval(interval);
    }
  }, 250);
}

type PrintWidgetBaseProps = Utils.WithTestIds;
interface PrintWidgetSlotProps {
  slotProps?: { button?: ButtonProps; icon?: SvgIconProps };
}

export type PrintWidgetProps = PrintWidgetBaseProps & PrintWidgetSlotProps;

interface PrintTableProps {
  headers: React.ReactNode[];
  rows: React.ReactNode[][];
}

function PrintTable({ headers, rows }: PrintTableProps) {
  return (
    <TableContainer id="print-widget-print">
      <Table>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
// todo: use `usePrint` to replace some of this logic
/** Must be used in the context of a `DataGrid`. */
export const PrintWidget = ({ slotProps }: PrintWidgetProps) => {
  const apiRef = useGridApiContext();
  const [portal, setPortal] = useState<React.ReactPortal>();

  const onClick = useCallback(
    function onClick() {
      const allRows = apiRef.current.getRowModels();
      const visibleRows: GridValidRowModel[] = [];
      Object.entries(apiRef.current.state.visibleRowsLookup).forEach(([rowId, isVisible]) => {
        const row = allRows.get(rowId);
        if (isVisible && row) {
          visibleRows.push(row);
        }
      });

      const visibleColumns = apiRef.current.getVisibleColumns();

      setPortal(() =>
        createPortal(
          <PrintTable
            headers={visibleColumns.map(({ field, headerName }) => headerName ?? field)}
            rows={visibleRows.map((row) => {
              return visibleColumns.map((colDef) => {
                const { field, renderCell } = colDef;
                if (renderCell) {
                  return renderCell(buildPrintRenderCellParams(apiRef.current, row, colDef));
                }
                return row[field];
              });
            })}
          />,
          document.body,
        ),
      );
      onPrint(() => setPortal(undefined));
    },
    [apiRef],
  );

  return (
    <>
      <Button
        aria-label="Print"
        color="primary"
        onClick={onClick}
        startIcon={<Print {...slotProps?.icon} />}
        variant="text"
        {...slotProps?.button}
      >
        Print
      </Button>
      {portal}
    </>
  );
};
